import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { MarkupCondition, PriceSuggestionCondition } from '@retail/calculus/types';
import { PriceSuggestionType } from '@retail/products/types';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { CalculusAreaAccordionDetails } from '../CalculusAreaAccordionDetails';
import { useMemo } from 'react';
import { SalesPriceAccordionSummary } from './SalesPriceAccordionSummary';
import { SalesPriceConditionPriority } from './SalesPriceConditionPriority';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';

interface Props {
  activePriceSuggestionType?: PriceSuggestionType;
  priceSuggestionCondition?: PriceSuggestionCondition;
  markupCondition?: MarkupCondition;
  priceSuggestionPrice?: number;
  markupPrice?: number;
  availablePriceSuggestions: PriceSuggestionPrice[];
  editSalesPriceCondition?: () => void;
}

export function SalesPriceConditionAccordion({
  activePriceSuggestionType,
  priceSuggestionCondition,
  markupCondition,
  priceSuggestionPrice,
  markupPrice,
  availablePriceSuggestions,
  editSalesPriceCondition,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { bindingType, validFrom, validTo, isInheritedMarkup } = useMemo(() => {
    return {
      bindingType: priceSuggestionCondition?.bindingType ?? markupCondition?.bindingType,
      validFrom: priceSuggestionCondition?.validFrom ?? markupCondition?.validFrom,
      validTo: priceSuggestionCondition?.validTo ?? markupCondition?.validTo,
      isInheritedMarkup:
        !!priceSuggestionCondition?.bindingTarget &&
        priceSuggestionCondition?.bindingType !== markupCondition?.bindingType,
    };
  }, [markupCondition, priceSuggestionCondition]);
  return (
    <Accordion>
      <AccordionSummary>
        <SalesPriceAccordionSummary
          priceSuggestionPriority={priceSuggestionCondition?.priceSuggestionPriorities ?? []}
          activePriceSuggestionType={activePriceSuggestionType}
          priceSuggestionPrice={priceSuggestionPrice}
          markupPrice={markupPrice}
          markupCoverageDegree={markupCondition?.coverageDegree}
          onEdit={editSalesPriceCondition}
        />
      </AccordionSummary>
      <AccordionDetails>
        {bindingType && validFrom && (
          <CalculusAreaAccordionDetails
            bindingType={bindingType}
            conditionValueDesc={t('myAssortment.columns.priority')}
            conditionValue={
              <SalesPriceConditionPriority
                priceSuggestionPriority={priceSuggestionCondition?.priceSuggestionPriorities ?? []}
                availablePriceSuggestions={availablePriceSuggestions}
                activePriceSuggestionType={activePriceSuggestionType}
                markupPrice={markupPrice}
                markupCoverageDegree={markupCondition?.coverageDegree}
                isInheritedMarkup={isInheritedMarkup}
              />
            }
            separateValueLine
            validFrom={validFrom}
            validTo={validTo}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
